'use client';

import { Trash } from '@/components/Icons/Trash';
import { useCart } from '@/components/commerce/cart/cart-context';
import { elevarRemoveFromCart } from '@/lib/elevar/cart-events';

export function DeleteItemButton({ item, position }) {
  const { deleteCartItem, cart } = useCart();
  const elevarRemoveItems = [item];

  const show_remove_button =
    item.attributes?.find((attribute) => attribute.key.includes('promoType'))
      ?.value !== 'free';

  function getLineItemIdsToRemove(lineItemToRemove) {
    const ids = new Set();
    ids.add(lineItemToRemove.id);

    // It will have a bundle id property if it is part of a bundle
    const bundleId = lineItemToRemove.attributes?.find(
      (attr) => attr.key === '_bundleId'
    )?.value;

    // Look for related items to remove if applicable
    cart?.lines.forEach((cartItem) => {
      const promoItemData = cartItem.attributes
        ?.filter((attr) => attr.key === '_promoParent')
        .map((attr) => ({
          promoParentValue: attr.value,
          cartItemId: cartItem.id
        }));

      // If this is a gwp item and the parent item is being removed, add the
      // gwp item to the list of items to remove
      if (
        promoItemData.length > 0 &&
        lineItemToRemove.merchandise.id === promoItemData[0]?.promoParentValue
      ) {
        ids.add(promoItemData[0]?.cartItemId);
        elevarRemoveItems.push(cartItem);
      }

      // If the item being removed is part of a bundle, add this item if it
      // is part of the same bundle to be removed
      if (bundleId) {
        const thisBundleId = cartItem.attributes?.find(
          (attr) => attr.key === '_bundleId'
        )?.value;
        if (thisBundleId === bundleId) {
          ids.add(cartItem.id);
          elevarRemoveItems.push(cartItem);
        }
      }
    });
    elevarRemoveFromCart(elevarRemoveItems, item?.quantity);
    return [...ids];
  }

  const handleRemove = async () => {
    // If the item doesn't have an ID, it means it's a new item and the cart is
    // still optimistic. Happens only if you remove a cart cross-sell
    // *immediately* after adding and within less than a second.
    // Used `warn` since it resolves right away and we shouldn't slow down other
    // cases that use this function just for this rare edge case.
    if (!item?.id && cart?.isOptimistic) {
      console.warn(
        'Attempted to remove an item without an ID, cart is still optimistic.'
      );
      return;
    }

    const productToRemove = { position, lineItemId: item.id, item };
    const allLineItemIds = getLineItemIdsToRemove(item);
    if (allLineItemIds.length > 0) {
      const result = await deleteCartItem(
        allLineItemIds,
        productToRemove.lineItemId
      );
      if (result?.error) {
        console.error('Error removing item from cart:', result.error);

        return;
      }
    }
  };

  return (
    <button
      onClick={handleRemove}
      aria-label={`Remove cart item ${item?.merchandise?.product?.title} ${item?.merchandise?.title}`}
      data-cy="remove-item"
      className={`flex cursor-pointer justify-end ${!show_remove_button ? 'invisible' : ''} expanded-hitbox h-5 w-5`}
    >
      <Trash className="mx-px h-4 w-4 text-white dark:text-black" />
    </button>
  );
}
